import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { Smile, Frown, X } from 'lucide-react';
import { Button } from '../ui/button';

const SentimentTrendline = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [trendData, setTrendData] = useState({ points: [] });
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [topCalls, setTopCalls] = useState({ positive: [], negative: [] });
  const [granularity, setGranularity] = useState('hour');

  useEffect(() => {
    fetchTrendData();
  }, [filters, granularity]);

  const fetchTrendData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/sentiment-trend?granularity=${granularity}`
      );
      const result = await response.json();
      setTrendData(result);
    } catch (error) {
      console.error('Error fetching sentiment trend:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePointClick = async (data) => {
    setSelectedPoint(data);
    setDrawerOpen(true);
    
    // Fetch top positive and negative calls for this time period
    // For now, we'll use mock data - in production, add API endpoint
    // GET /api/analytics/sentiment-calls?timestamp=X&limit=5
    setTopCalls({
      positive: [
        { id: '1', transcript: 'This is amazing! I love it.', sentiment: 0.92 },
        { id: '2', transcript: 'Great service, thank you!', sentiment: 0.85 },
        { id: '3', transcript: 'Very helpful and professional.', sentiment: 0.78 },
        { id: '4', transcript: 'Excellent experience.', sentiment: 0.72 },
        { id: '5', transcript: 'Good call, informative.', sentiment: 0.68 }
      ],
      negative: [
        { id: '6', transcript: 'This is frustrating.', sentiment: -0.82 },
        { id: '7', transcript: 'Not helpful at all.', sentiment: -0.74 },
        { id: '8', transcript: 'Waste of my time.', sentiment: -0.68 },
        { id: '9', transcript: 'I asked to be removed.', sentiment: -0.65 },
        { id: '10', transcript: 'Not interested, stop calling.', sentiment: -0.58 }
      ]
    });
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment > 0.3) return '#10b981';  // Green (positive)
    if (sentiment > 0) return '#84cc16';    // Lime (slightly positive)
    if (sentiment === 0) return '#6b7280';  // Gray (neutral)
    if (sentiment > -0.3) return '#f59e0b'; // Amber (slightly negative)
    return '#ef4444';                        // Red (negative)
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const sentiment = data.avg_sentiment;
      const color = getSentimentColor(sentiment);
      
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
          <p className="text-sm font-semibold text-slate-200">
            {new Date(data.ts).toLocaleString()}
          </p>
          <p className="text-xs mt-1" style={{ color }}>
            Sentiment: {sentiment.toFixed(3)}
          </p>
          <p className="text-xs text-slate-500">
            Calls: {data.count}
          </p>
          <p className="text-xs text-gold mt-2">
            Click to see top positive/negative calls
          </p>
        </div>
      );
    }
    return null;
  };

  const formattedData = trendData.points.map(p => ({
    ...p,
    timestamp: new Date(p.ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: granularity === 'hour' ? 'numeric' : undefined
    })
  }));

  return (
    <>
      <Card className="glass-card" data-testid="sentiment-trendline">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Smile className="w-5 h-5 text-emerald-400" />
                Sentiment Over Time
              </CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Customer sentiment trend (-1 negative to +1 positive) • Click point for details
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setGranularity('hour')}
                className={`px-3 py-1 text-xs rounded transition-all ${
                  granularity === 'hour'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Hourly
              </button>
              <button
                onClick={() => setGranularity('day')}
                className={`px-3 py-1 text-xs rounded transition-all ${
                  granularity === 'day'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Daily
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-80">
              <div className="animate-pulse text-slate-400">Loading sentiment data...</div>
            </div>
          ) : formattedData.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <p>No sentiment data available yet</p>
              <p className="text-xs mt-2">Sentiment scores will appear once transcripts are processed</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={formattedData} onClick={(e) => e?.activePayload && handlePointClick(e.activePayload[0].payload)}>
                <defs>
                  <linearGradient id="sentiment-positive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="sentiment-negative" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="timestamp"
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  domain={[-1, 1]}
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Sentiment Score', angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
                />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#C9A24A', strokeWidth: 2 }} />
                <Line
                  type="monotone"
                  dataKey="avg_sentiment"
                  stroke="#C9A24A"
                  strokeWidth={3}
                  dot={{ fill: '#C9A24A', r: 5, cursor: 'pointer' }}
                  activeDot={{ r: 7, fill: '#C9A24A', stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
                  animationDuration={reducedMotion ? 0 : 600}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>
          )}

          {/* Average Sentiment Summary */}
          {formattedData.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="bg-emerald-900/20 border border-emerald-600/30 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-400">
                  {(() => {
                    const avg = formattedData.reduce((sum, d) => sum + d.avg_sentiment, 0) / formattedData.length;
                    return avg.toFixed(2);
                  })()}
                </div>
                <div className="text-xs text-slate-400">Average Sentiment</div>
              </div>
              <div className="bg-gold border border-gold/40 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gold">
                  {formattedData.filter(d => d.avg_sentiment > 0).length}
                </div>
                <div className="text-xs text-slate-400">Positive Periods</div>
              </div>
              <div className="bg-rose-900/20 border border-rose-600/30 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-rose-400">
                  {formattedData.filter(d => d.avg_sentiment < 0).length}
                </div>
                <div className="text-xs text-slate-400">Negative Periods</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Detail Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
          >
            <motion.div
              className="bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto"
              initial={reducedMotion ? {} : { scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-200">
                    Top Calls for {selectedPoint && new Date(selectedPoint.ts).toLocaleString()}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Positive */}
                  <div>
                    <h4 className="flex items-center gap-2 text-lg font-semibold text-emerald-400 mb-3">
                      <Smile className="w-5 h-5" />
                      Top 5 Positive
                    </h4>
                    <div className="space-y-3">
                      {topCalls.positive.map((call, idx) => (
                        <motion.div
                          key={call.id}
                          className="bg-emerald-900/20 border border-emerald-600/30 rounded-lg p-3"
                          initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-emerald-400">
                              Sentiment: {call.sentiment.toFixed(2)}
                            </span>
                            <span className="text-xs text-slate-500">#{idx + 1}</span>
                          </div>
                          <p className="text-sm text-slate-300">"{call.transcript}"</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Top Negative */}
                  <div>
                    <h4 className="flex items-center gap-2 text-lg font-semibold text-rose-400 mb-3">
                      <Frown className="w-5 h-5" />
                      Top 5 Negative
                    </h4>
                    <div className="space-y-3">
                      {topCalls.negative.map((call, idx) => (
                        <motion.div
                          key={call.id}
                          className="bg-rose-900/20 border border-rose-600/30 rounded-lg p-3"
                          initial={reducedMotion ? {} : { opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-rose-400">
                              Sentiment: {call.sentiment.toFixed(2)}
                            </span>
                            <span className="text-xs text-slate-500">#{idx + 1}</span>
                          </div>
                          <p className="text-sm text-slate-300">"{call.transcript}"</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>  );
};

export default SentimentTrendline;
